import { _ } from "../../../polyglot/polyglot.js";
import { nameLabel } from "../../../../models/wysiwygEditor/builders/helpers.js";
import { periodSectionRenderer } from "./popoverPeriodSectionBuilder.js";
import { router } from "@foretagsplatsen/widgetjs";
import displaySectionBuilder from "./displaySectionBuilder.js";
import makeInputValuePopover from "./makeInputValuePopover.js";
import precisionSectionRenderer from "./precisionSectionBuilder.js";

/**
 * Create a popover that shows when clicking on a KPI input value.
 *
 * @param {function} getPeriod - Function returning the base period used to fetch data
 * @param {bool} isBimesterEnabled - When true, the period type dropdown shows Bimester
 * @param {PeriodType} defaultPeriodType - Period type used by default
 * @param {function} updateNodeAttributes - Function used to update a ProseMirror node attributes
 * @param {function():DOMElement} getTriggerItem - Returns the element
 *   that will trigger the popover on click
 * @param {object} content - Value fetched by the input value
 * @param {KeyFigureResult} content.value - Value of the keyFigure
 * @param {KeyFigure} content.keyFigure - Fetched keyFigure
 * @param {string} content.precision - Precision to use to format the Keyfigure value
 * @param {number} content.shift - Number of period shifts
 * @param {PeriodType} content.shiftPeriodType - Type of period shift
 * @param {PeriodType} content.periodType - Fetched period type
 */
export default function makeKeyFigureInputValuePopover({
	isEditor = false,
} = {}) {
	return function ({
		getPeriod,
		isBimesterEnabled,
		defaultPeriodType,
		updateNodeAttributes,
		getTriggerItem,
		content: {
			keyFigure,
			precision,
			shift,
			shiftPeriodType,
			periodType,
			display,
		},
	}) {
		let localDisplay = display;
		const shouldShowFn = () => localDisplay !== nameLabel;
		const currencyPrecisionRegion = precisionSectionRenderer({
			precision,
			shouldShowFn,
			onChange: (value) => {
				updateNodeAttributes({
					precision: value,
				});
			},
		});

		const periodSection = periodSectionRenderer({
			isYearToDateEnabled: true,
			isBimesterEnabled,
			shift,
			shiftPeriodType,
			updateNodeAttributes,
			defaultPeriodType,
			periodType,
			shouldShowFn,
			canShift: !getPeriod().isConsecutiveMonths(),
		});

		const popoverWidget = makeInputValuePopover({
			triggerItem: getTriggerItem(),
			header: (html) => {
				html.span(
					html.strong(keyFigure.getName()),
					" - ",
				).renderNonBreaking(getPeriod().toSmartString());
			},
			content: (html) => {
				html.div(
					html.div(
						{ class: "description" },
						keyFigure.getDescription(),
					),
					displaySectionBuilder({
						display,
						onChange: (value) => {
							updateNodeAttributes({
								display: value,
							});

							localDisplay = value;
							currencyPrecisionRegion.update();
							periodSection.update();
						},
					}),
					currencyPrecisionRegion,
					periodSection,
				);
			},
			extraFooterAction:
				!isEditor &&
				((html) => {
					const { linkTo } = router.getRouter();

					html.a(
						{
							click: () => popoverWidget.hide(),
							href: linkTo("single-kpi", {
								period: getPeriod().toString(),
								keyFigure: keyFigure.getId(),
							}),
							target: "_blank",
							class: "btn btn-link",
							"data-test": "apply",
						},
						_("View KPI"),
					);
				}),
		});

		return popoverWidget;
	};
}
